// The gift.channels control: three boxes and a line saying what they add up to.
// Ports .codelane/artifacts/t-125/config-gifts.html L333-L341.
//
// Checkboxes rather than switches, as the mockup argues (L545-L548): this is a
// multiple choice out of three, not three independent features, and a switch in
// this panel means "turn a subsystem on" (Facebook auth, the profanity filter).

import { Checkbox } from "../../../components/Checkbox/Checkbox";
import { GIFT_CHANNELS } from "./defaults";
import type { GiftChannel } from "./types";
import styles from "./GiftChannelsField.module.css";

/**
 * What each channel is called in the panel. A `Record` over the union rather
 * than a list of pairs, so a fourth channel cannot be added to the type without
 * the compiler asking what to call it.
 */
const CHANNEL_LABELS: Record<GiftChannel, string> = {
  friends: "Friends",
  guild: "Guildmates",
  code: "Anyone by friend code",
};

interface Props {
  value: GiftChannel[];
  onChange: (next: GiftChannel[]) => void;
}

export function GiftChannelsField({ value, onChange }: Props) {
  const checked = new Set(value);

  /**
   * Rebuild the list from the canonical order rather than pushing onto the end
   * of it, so a set of channels has ONE spelling however it was assembled.
   * `normalizeGiftChannels` says the same thing about a stored list, and the
   * save bar diffs through stable JSON — two orders of the same three names
   * would read as an edit that never goes away.
   */
  function toggle(channel: GiftChannel, next: boolean) {
    const wanted = new Set(checked);
    if (next) wanted.add(channel);
    else wanted.delete(channel);
    onChange(GIFT_CHANNELS.filter((c) => wanted.has(c)));
  }

  return (
    <>
      <div className={styles.channels}>
        {GIFT_CHANNELS.map((channel) => (
          <Checkbox
            key={channel}
            checked={checked.has(channel)}
            onChange={(next) => toggle(channel, next)}
            label={
              <>
                {CHANNEL_LABELS[channel]}{" "}
                <span className={styles.cbxApi}>{channel}</span>
              </>
            }
            // The visible text carries the api name as well, which is an
            // annotation and not part of what the box is called. Naming it
            // after the channel alone is what a screen reader should hear.
            aria-label={CHANNEL_LABELS[channel]}
          />
        ))}
      </div>
      {/* An empty allow-list is the section's default and its most consequential
          state — every send refused — so it is said outright rather than left
          to be inferred from three empty boxes.
          The line belongs to THAT state and to no other: the mockup writes one
          helper sentence (L341) and its own states section (L522) attaches it
          to «ни одного канала» alone. With a box checked the boxes say what
          the set is, and a second sentence restating them would be copy this
          page was not designed with. */}
      {value.length === 0 && (
        <div className={styles.helper}>
          Nothing checked — gifts are off for this project.
        </div>
      )}
    </>
  );
}
